
import { Alert, AlertDescription } from '@/components/ui/alert';
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { DateTimePicker } from '@/components/ui/date-time-picker';
import { Separator } from '@/components/ui/separator';
import { Calendar, Timer } from 'lucide-react';
import type { UseFormReturn } from 'react-hook-form';
import type { IncidentFormData } from './types';

interface DatesAndSlaSectionProps {
  form: UseFormReturn<IncidentFormData>;
  isEditMode: boolean;
  isViewMode?: boolean;
}

export function DatesAndSlaSection({ form, isEditMode, isViewMode = false }: DatesAndSlaSectionProps) {
  return (
    <div className="space-y-6">
      <Alert>
        <Calendar className="h-4 w-4" />
        <AlertDescription>
          Ciclo de vida temporal y cumplimiento de niveles de servicio
        </AlertDescription>
      </Alert>

      {/* Key Dates */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="reportedAt"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Fecha de Reporte *</FormLabel>
              <FormControl>
                <DateTimePicker
                  value={field.value || null}
                  onChange={field.onChange}
                  label="Fecha de Reporte"
                  disabled={isViewMode}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="detectedAt"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Fecha de Detección</FormLabel>
              <FormControl>
                <DateTimePicker
                  value={field.value || null}
                  onChange={field.onChange}
                  label="Fecha de Detección"
                  disabled={isViewMode}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <Separator />

      {/* SLA & Due Date */}
      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
          <Timer className="h-4 w-4" /> SLA y Vencimiento
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="dueAt"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Fecha de Vencimiento (SLA)</FormLabel>
                <FormControl>
                  <DateTimePicker
                    value={field.value || null}
                    onChange={field.onChange}
                    label="Vencimiento"
                    disabled={isViewMode}
                  />
                </FormControl>
                <FormDescription>Límite para resolución</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="slaPolicyId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Política SLA (Legacy)</FormLabel>
                <FormControl>
                  <input
                    type="text"
                    value={field.value || ''}
                    onChange={field.onChange}
                    placeholder="UUID..."
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                    disabled
                  />
                </FormControl>
                <FormDescription>Calculado automáticamente</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>

      {/* Resolution Dates (Only in Edit/View) */}
      {(isEditMode || isViewMode) && (
        <>
          <Separator />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="resolvedAt"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Fecha de Resolución</FormLabel>
                  <FormControl>
                    <DateTimePicker
                      value={field.value || null}
                      onChange={field.onChange}
                      label="Resolución"
                      disabled={isViewMode}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="closedAt"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Fecha de Cierre</FormLabel>
                  <FormControl>
                    <DateTimePicker
                      value={field.value || null}
                      onChange={field.onChange}
                      label="Cierre"
                      disabled={isViewMode}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </>
      )}
    </div>
  );
}
